import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useIsDesktop } from '@/lib/notifications/useIsDesktop';

export interface PopupShellProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Overrides the shared testid (default `notification-detail`, kept for existing specs). */
  testId?: string;
  className?: string;
}

/** The shared inner card layout — a centered Outfit column, gap-36, per the global pop-up system. */
function ShellColumn({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col items-center gap-9 text-center font-sans text-ink', className)}>{children}</div>;
}

/**
 * The reusable bottom-sheet card shell every ARC-111 pop-up view renders into.
 * Keeps the notification surface's responsive split — a slide-up drag-to-dismiss
 * bottom sheet on mobile, a centered dialog on desktop — and applies the Figma
 * card system to it: 450px white card, 1px border, radius 20, drop shadow, a
 * 48×4 drag handle (mobile), centered Outfit column.
 *
 * Views compose their own content order inside (medallion / title / body / CTA),
 * so a view like withdrawal-failed can put its title above the medallion.
 */
export function PopupShell({ open, onClose, children, testId = 'notification-detail', className }: PopupShellProps) {
  const isDesktop = useIsDesktop();

  if (!isDesktop) {
    return (
      <BottomSheet open={open} onClose={onClose} size="sm" data-testid={testId} className={cn('px-3.5 pb-14', className)}>
        <ShellColumn className="pt-8">{children}</ShellColumn>
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      data-testid={testId}
      className={cn('max-w-[450px] px-3.5 py-14', className)}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-muted hover:text-primary"
      >
        <X className="size-4" />
      </button>
      <ShellColumn>{children}</ShellColumn>
    </Dialog>
  );
}
